import { ModalComponent } from '../../../ui/modal-component/modal-component.component';
import { Component, DestroyRef, EventEmitter, inject, Input, Output, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormWrapperComponent } from '../../../ui/form-wrapper/form-wrapper.component';
import { InputFieldComponent } from '../../../ui/inputField/inputField.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormBuilder, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { MessageSchedule, ScheduleService } from '../../../../services/campaign/schedule.service';
import { MessagesService } from '../../../../services/campaign/messages.service';
import { TimeZoneService } from '../../../../services/timezone/time-zone.service';
import {
  browserTimeZone,
  formatRun,
  scheduleMoment,
} from '../../../../pages/organization/campaigns/campaign-list/campaign-facts';

@Component({
  selector: 'app-edit-schedule-form',
  standalone: true,
  imports: [FormWrapperComponent, ModalComponent, InputFieldComponent, MatTooltipModule, ReactiveFormsModule],
  templateUrl: './edit-schedule-form.component.html',
  styleUrl: '../campaign-form.css',
})
export class EditScheduleFormComponent implements OnDestroy {
  @Output() closePopupEvent = new EventEmitter<boolean>();
  @Input({ required: true }) open: boolean = false;
  scheduleService = inject(ScheduleService);
  messagesService = inject(MessagesService);
  private timeZoneService = inject(TimeZoneService);
  private destroyRef = inject(DestroyRef);

  messageSchedule = this.fb.group({
    message: ['', Validators.required],
    messageDates: this.fb.array([]),
  });

  scheduleToEdit!: MessageSchedule | undefined;
  scheduleId!: string;

  /** Rows added here start in the browser's zone, which joins the list if it is missing. */
  private readonly defaultZone = browserTimeZone();
  readonly timeZones = (() => {
    const list = this.timeZoneService.getTimeZones();
    return list.some((tz) => tz.timezone === this.defaultZone)
      ? list
      : [{ timezone: this.defaultZone, offset: '' }, ...list];
  })();

  // timeout ID for cleanup
  private scheduleTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(private fb: FormBuilder, private router: Router, private route: ActivatedRoute) {}

  /** The subject of the message these times belong to, as it reads now. */
  get messageSubject(): string {
    const id = this.scheduleToEdit?.message?.id;
    const current = this.messagesService.savedMessages.find((message) => message.id === id);
    return current?.subject || this.scheduleToEdit?.message?.subject || 'Untitled message';
  }

  get campaignRange(): string {
    return formatRun(this.campaignStart, this.campaignEnd);
  }

  removeQueryParam() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { edit: null }, // Set the param you want to remove to null
      queryParamsHandling: 'merge', // Merge with the existing query params
    });
  }

  close() {
    this.removeQueryParam();
    this.closePopupEvent.emit(false);
  }

  get messageDates() {
    return this.messageSchedule.get('messageDates') as FormArray;
  }

  private dateRow(date = '', time = '', timezone: string | null = this.defaultZone) {
    // Every row keeps its time zone, so an edited time is never sent without one.
    return this.fb.group({
      date: [date, Validators.required],
      time: [time, Validators.required],
      timezone: [timezone || this.defaultZone, Validators.required],
    });
  }

  addMessageDates() {
    this.messageDates.push(this.dateRow());
  }

  removeMessageDates(index: number) {
    this.messageDates.removeAt(index);
  }

  campaignStart = this.scheduleService.campaignSchedule.campaignStart;
  campaignEnd = this.scheduleService.campaignSchedule.campaignEnd;

  scheduleSubmitted = false; //for controlling errors on schedule message form
  saveSchedule() {
    this.scheduleSubmitted = true; // for controlling errors on form
    if (this.messageSchedule.valid) {
      // get message from message service with message id
      let message = this.messagesService.savedMessages.find(
        (item) => item.id == this.messageSchedule.value.message
      );

      // check if message schedule object already exist in schedule service and return index
      const index = this.scheduleService.messageSchedule.findIndex((item) => item.id == this.scheduleId);

      // if index exists, replace in service
      if (index !== -1) {
        const updatedMessage = {
          id: this.scheduleId,
          messageDates: this.messageDates.value,
          message,
        };

        this.scheduleService.messageSchedule[index] = updatedMessage;
      }

      // reset input fields
      this.scheduleSubmitted = false;
      this.messageSchedule = this.fb.group({
        message: [''],
        messageDates: this.fb.array([]),
      });
      this.close();
    }

    // to remove errors
    if (this.scheduleTimeoutId) {
      clearTimeout(this.scheduleTimeoutId);
    }
    this.scheduleTimeoutId = setTimeout(() => {
      this.scheduleSubmitted = false;
      this.scheduleTimeoutId = undefined;
    }, 5000);
  }

  deleteSchedule() {
    const index = this.scheduleService.messageSchedule.findIndex((item) => item.id == this.scheduleId);
    if (index !== -1) {
      // Remove the item from the array
      this.scheduleService.messageSchedule.splice(index, 1);
    }
    this.close();
  }

  // validations
  validateMessageDate(index: number) {
    if (this.campaignStart && this.campaignEnd) {
      const inputControl = this.messageDates.at(index).get('date');
      if (inputControl?.value) {
        const startDate = new Date(this.campaignStart);
        const endDate = new Date(this.campaignEnd);
        const inputDate = new Date(inputControl.value);
        if (startDate <= inputDate && inputDate <= endDate) {
          inputControl?.setErrors(null);
        } else {
          inputControl?.setErrors({ outOfRange: true });
        }
      }
    }
  }

  /** A send time that has already passed, in its own time zone, cannot be saved. */
  validateMessageTime(index: number) {
    const row = this.messageDates.at(index);
    const date = row.get('date')?.value;
    const time = row.get('time');
    if (!date || !time?.value) return;

    const at = scheduleMoment(date, time.value, row.get('timezone')?.value);
    const { past, ...others } = time.errors ?? {};
    if (at && at.getTime() <= Date.now()) {
      time.setErrors({ ...others, past: true });
    } else if (past) {
      time.setErrors(Object.keys(others).length ? others : null);
    }
  }

  setMessageDates(dates?: { date: string | null; time: string | null; timezone?: string | null }[]) {
    dates?.forEach((date) => {
      this.messageDates.push(this.dateRow(date.date ?? '', date.time ?? '', date.timezone ?? null));
    });
  }

  ngOnInit() {
    // get schedule to edits id and fetch schedule from schedule service to initialize form data
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.scheduleId = params['edit'];
      this.scheduleToEdit = this.scheduleService.messageSchedule.find((item) => item.id == this.scheduleId);

      // initialize form values
      this.messageSchedule.patchValue({
        message: this.scheduleToEdit?.message?.id,
      });

      // initialize messageDates
      this.messageDates.clear();
      this.setMessageDates(this.scheduleToEdit?.messageDates);
    });
  }

  ngOnDestroy(): void {
    // Clear timeout when component is destroyed
    if (this.scheduleTimeoutId) {
      clearTimeout(this.scheduleTimeoutId);
    }
  }
}
